import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useCommsRefresh } from 'core/hooks';
import { Colors } from 'services/ColorService';

import { SlackWorkspaceConfig } from '../../_detailShared/SlackWorkspaceConfig';
import { SlackManagerTokenConfig } from '../../_detailShared/SlackManagerTokenConfig';
import { SlackMessagesPanel } from '../../_detailShared/SlackMessagesPanel';
import { SlackMemberMatchPanel } from '../../_detailShared/SlackMemberMatchPanel';
import { NotesPanel } from '../../_detailShared/NotesPanel';

interface ICommunicationTabProps {
  groupId: string;
  /** Preselect the feed's per-channel filter (dashboard `?conv=` deep link). */
  initialChannelId?: string;
}

const HeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const RefreshButton = styled.button`
  padding: 6px 14px;
  border: 1px solid ${Colors.Blue};
  border-radius: 6px;
  background: ${Colors.White};
  color: ${Colors.Blue};
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/**
 * Group "Общение" tab (workspace-per-group rework) — the interaction hub:
 * per-group Slack workspace config + the unified newest-first message feed
 * across all channels (read/flag, filters, pagination) + notes. Tasks live in
 * their own "Задачи" tab. The refresh button syncs THIS group's Slack
 * workspace + the shared mailboxes on demand (hourly beat + the real-time
 * Slack webhook cover the rest of the time).
 */
export const CommunicationTab = memo(
  ({ groupId, initialChannelId }: ICommunicationTabProps) => {
    const { t } = useTranslation();
    const { refresh, syncing } = useCommsRefresh(groupId);
    return (
      <div>
        <HeaderRow>
          <RefreshButton type="button" disabled={syncing} onClick={refresh}>
            {t('pages.communications.syncGroup')}
          </RefreshButton>
        </HeaderRow>
        <SlackWorkspaceConfig groupId={groupId} />
        <SlackManagerTokenConfig groupId={groupId} />
        <SlackMessagesPanel
          groupId={groupId}
          initialChannelId={initialChannelId}
        />
        <SlackMemberMatchPanel groupId={groupId} />
        <NotesPanel groupId={groupId} />
      </div>
    );
  },
);

CommunicationTab.displayName = 'CommunicationTab';
